import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { ReactComponent as DropdownIcon } from 'images/common/dropdown.svg'
import { ReactComponent as ActiveIcon } from 'images/checkbox/active.svg'
import { ReactComponent as InactiveIcon } from 'images/checkbox/inactive.svg'
import { ReactComponent as SearchIcon } from 'images/stations-selector/search.svg'
import { stationMatches } from 'utils/search'
import classNames from 'classnames/bind'
import styles from './MultiSelect.module.scss'
import { images } from 'images/image-library'
import { ImageAsset } from '../image-asset'

const cx = classNames.bind(styles)

/**
 * Retrieves a specific value from an item object by matching its key.
 *
 * @param {string} item - The key used to find the matching item in `itemDefinitions`.
 * @param {Array<Object>} itemDefinitions - The full list of item objects, each containing a `key` property.
 * @param {string} keyToReturn - The property name whose value should be returned from the matched item.
 *
 * @returns {*} The value of the requested property from the matched item,  
 *              or `undefined` if no matching item is found.
 */
const getItemKeyValue = (item, itemDefinitions, keyToReturn) => {
  const filteredItem = itemDefinitions.find(itemDefinition => itemDefinition.key === item)

  if(!filteredItem) return

  return filteredItem[keyToReturn]
}

const getItemDefinition = (item, itemDefinitions) => {
  return itemDefinitions.find(itemDefinition => itemDefinition.key === item)
}

const getResolvedItems = (items, itemDefinitions) => {
  const expandedItems = items.flatMap(item => {
    const itemDefinition = getItemDefinition(item, itemDefinitions)
    return itemDefinition?.toggleItems?.length ? itemDefinition.toggleItems : [item]
  })

  // Remove duplicates that may arise from overlapping toggle groups
  return [...new Set(expandedItems)]
}

const renderItems = (
  items,
  value,
  onValueSelect,
  minSelected,
  onLessThanMinSelected,
  maxSelected,
  onMoreThanMaxSelected,
  search,
  singular,
  setOpen,
  itemDefinitions,
  webAssetsKey,
) => {
  if (search) {
    items = items.filter(s => stationMatches(s, search))
  }

  return items.map(item => {
    const itemDefinition = getItemDefinition(item, itemDefinitions)
    const toggleItems = itemDefinition?.toggleItems ?? [] // Only relevant for 'parent' toggle groups
    const isToggleGroup = toggleItems.length > 0

    const handleClick = event => {
      let newValue = value.slice()

      if (singular) {
        onValueSelect([item]);
        setOpen(false);
        return;
      }

      if (isToggleGroup) {
        const areAllGroupedItemsSelected = toggleItems.every(groupedItem => newValue.includes(groupedItem))

        const deselectAll = (values, items) => values.filter(v => !items.includes(v))
        const selectAll = (values, items) => [...new Set([...values, ...items])]

        newValue = areAllGroupedItemsSelected
          ? deselectAll(newValue, toggleItems)
          : selectAll(newValue, toggleItems)
      } else if (newValue.includes(item)) {
        const index = newValue.indexOf(item)
        newValue.splice(index, 1)
      } else {
        newValue = [...newValue, item]
      }
      newValue.sort((a, b) => {
        const ai = items.indexOf(a)
        const bi = items.indexOf(b)
        return ai - bi
      })

      if (newValue.length < minSelected) {
        if (onLessThanMinSelected) {
          onLessThanMinSelected(event)
        }
      } else if (newValue.length > maxSelected) {
        if (onMoreThanMaxSelected) {
          onMoreThanMaxSelected(event)
        }
      } else {
        onValueSelect(newValue.slice())
      }
    }

    const itemName = getItemKeyValue(item, itemDefinitions, 'name')
    const itemKey = getItemKeyValue(item, itemDefinitions, 'key')
    // For toggle groups, check if all grouped items are selected to determine if the group is selected
    const isSelected = isToggleGroup
      ? toggleItems.every(groupedItem => value.includes(groupedItem))
      : value.includes(item)
    let itemIconSrc
    if(itemKey && webAssetsKey && images[webAssetsKey] && itemDefinition?.showIcon !== false) {
      itemIconSrc = images[webAssetsKey](itemKey)
    }

    return (
      <div
        key={item}
        className={cx('item', { 'with-icon': itemIconSrc, 'is-toggle-group': isToggleGroup })}
        style={itemDefinition?.indentLevel ? { paddingLeft: `${itemDefinition.indentLevel * 24}px` } : null}
        onClick={handleClick}
        data-testid="item"
      >
        {
          itemIconSrc && 
            <ImageAsset
              height={20}
              width={20}
              src={itemIconSrc}
              alt={itemKey}
            />
        }
        <span className={cx('item-container')}>{itemName ? itemName : item}</span>
        <div className={cx('checkbox-wrapper')}>
          {
            singular ? 
              (value === item ? <ActiveIcon /> : <InactiveIcon />) :
              (isSelected ? <ActiveIcon /> : <InactiveIcon />)
          }
        </div>
      </div>
    )
  })
}

const MultiSelect = ({
  value,
  items,
  itemDefinitions,
  webAssetsKey,
  onValueSelect,
  customClass,
  placeholder,
  maxHeight,
  disabled,
  allItemsText,
  leftAccessory,
  minSelected,
  maxSelected,
  onLessThanMinSelected,
  onMoreThanMaxSelected,
  onDropdownClick,
  selectButtons,
  viewSearch,
  children,
  customHandleSelectAll,
  customHandleDeselectAll,
  dataTestId,
  singular
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const resolvedItems = getResolvedItems(items, itemDefinitions)

  const handleDropdownClick = () => {
    if (!disabled) {
      onDropdownClick(open)
      setOpen(!open)
    }
  }

  const handleItemClick = event => {
    event.stopPropagation()
  }

  const handleSelectAll = () => {
    onValueSelect(resolvedItems)
    viewSearch && setSearch('')
    customHandleSelectAll?.()
  }

  const handleDeselectAll = () => {
    onValueSelect([])
    viewSearch && setSearch('')
    customHandleDeselectAll?.()
  }

  const handleSearchChange = event => {
    const search = event.target.value.replace(/\s+/g, ' ')
    setSearch(search)
  }

  useEffect(() => {
    if (singular && items.length > 0) {
      onValueSelect(items[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showPlaceholder = value.length === 0

  // Convert keys to names (fallback to key)
  const getDisplayNames = (value, itemDefinitions) => {
    return value.map(item => getItemKeyValue(item, itemDefinitions, 'name') || item).join(', ');
  }

  let displayedValue

  if (showPlaceholder) {
    displayedValue = placeholder
  } else if (!singular && value.length === resolvedItems.length) {
    displayedValue = allItemsText ?? getDisplayNames(value, itemDefinitions)
  } else if (!singular && value.length !== resolvedItems.length) {
    displayedValue = getDisplayNames(value, itemDefinitions)
  } else if (singular) {
    displayedValue = value
  }

  return (
    <div
      className={cx('dropdown', customClass, {
        disabled,
        open,
      })}
      onClick={handleDropdownClick}
      data-testid={dataTestId || "multi-select"}
    >
      <span className={cx({ placeholder: showPlaceholder })}>
        {leftAccessory} {displayedValue}
      </span>
      {!disabled && <DropdownIcon className={cx('dropdown-icon')} />}
      {open && (
        <div className={cx('items')} onClick={handleItemClick}>
          <div className={cx('item')} onClick={handleDropdownClick}>
            <span className={cx({ placeholder: showPlaceholder })}>
              {leftAccessory} {displayedValue}
            </span>
            <DropdownIcon className={cx('dropdown-icon')} />
          </div>
          <div
            className={cx('items-list')}
            data-testid="items-list"
            style={{
              maxHeight,
            }}
          >
            {viewSearch && (
              <div className={cx('search')}>
                <input
                  type="text"
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search for a region"
                  data-testid="search"
                />
                <SearchIcon />
              </div>
            )}
            {renderItems(
              items,
              value,
              onValueSelect,
              minSelected,
              onLessThanMinSelected,
              maxSelected,
              onMoreThanMaxSelected,
              search,
              singular,
              setOpen,
              itemDefinitions,
              webAssetsKey,
            )}
          </div>
          {selectButtons && (
            <div className={cx('selection-helpers')}>
              <button
                onClick={handleSelectAll}
                data-testid="select-all"
              >
                {t('widgets.stationSelector.selectAll')}
              </button>
              <button
                onClick={handleDeselectAll}
                data-testid="deselect-all"
              >
                {t('widgets.stationSelector.deselectAll')}
              </button>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  )
}

MultiSelect.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  items: PropTypes.arrayOf(PropTypes.string),
  onValueSelect: PropTypes.func,
  customClass: PropTypes.string,
  placeholder: PropTypes.string,
  maxHeight: PropTypes.string,
  allItemsText: PropTypes.string,
  disabled: PropTypes.bool,
  leftAccessory: PropTypes.node,
  minSelected: PropTypes.number,
  onLessThanMinSelected: PropTypes.func,
  maxSelected: PropTypes.number,
  onMoreThanMaxSelected: PropTypes.func,
  onDropdownClick: PropTypes.func,
  selectButtons: PropTypes.bool,
  viewSearch: PropTypes.bool,
  customHandleSelectAll: PropTypes.func,
  customHandleDeselectAll: PropTypes.func,
  singular: PropTypes.bool,
  itemDefinitions: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      indentLevel: PropTypes.number,
      showIcon: PropTypes.bool,
      toggleItems: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  webAssetsKey: PropTypes.string,
  dataTestId: PropTypes.string
}

MultiSelect.defaultProps = {
  value: [],
  items: [],
  onValueSelect: null,
  customClass: null,
  placeholder: null,
  maxHeight: null,
  allItemsText: null,
  disabled: false,
  leftAccessory: null,
  minSelected: 0,
  maxSelected: 100,
  onLessThanMinSelected: null,
  onMoreThanMaxSelected: null,
  onDropdownClick: null,
  selectButtons: false,
  viewSearch: false,
  customHandleSelectAll: null,
  customHandleDeselectAll: null,
  singular: false,
  itemDefinitions: [],
  webAssetsKey: null,
}

export { MultiSelect }
